import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import dabaduLogo from 'assets/images/trade-in/dabadu-logo.png';
import shape1 from 'assets/images/trade-in/search-shape-1.png';
import shape2 from 'assets/images/trade-in/search-shape-2.png';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import CommonAutocomplete from 'sharedComponents/inputGroup/Autocomplete';
import { clearVehicleAutoCompleteData, vehicleLookupAutocomplete } from 'store/TradeInState';
import { RootState } from 'store/store';
import { CarInformation } from '../TradeInInterface';
import Logger from 'utils/logger';

const VehicleSearch: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext }, ref) => {
  const { version } = useParams();
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  // React Hook Form
  const { control, setValue } = useFormContext();

  const { vehicleAutoCompleteData } = useSelector((state: RootState) => state.tradeInStore);
  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};

  const onSuggestionSelected = (suggestion: CarInformation) => {
    const vehicleData = {
      year: suggestion.model_year,
      make: suggestion.make,
      model: suggestion.model,
      trim: suggestion.series,
      heading: suggestion.description,
      uvc: suggestion.uvc,
    };

    setValue('vehicleSearch', vehicleData);

    Logger.custom('USER_SEARCH_VEHICLE_INFO', {
      uvc: suggestion.uvc,
      vehicle: suggestion.description,
      client: authenticateClientData?.data?.client || '',
      widgetName: authenticateClientData?.data?.widgetName || '',
      module: 'Trade-In',
    });

    onNext?.();
  };

  const fetchOptions = async (searchText: string): Promise<[]> => {
    // Fetch filtered data based on the search text
    try {
      searchText.length < 2 && dispatch(clearVehicleAutoCompleteData());
      searchText.length > 2 && dispatch(vehicleLookupAutocomplete(searchText));
    } catch (error) {
      console.error('Error fetching search results:', error);
    }

    return [];
  };

  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    onNext?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
  }));

  return (
    <>
      <div className='trade-in-search'>
        <div className='trade-in-search__lbl'>{t('stepperFormTradeIn.freeTradeInEstimate')}</div>
        <div className='form-group'>
          <CommonAutocomplete
            control={control}
            name='vehicleSearch'
            fetchOptions={fetchOptions}
            filteredData={vehicleAutoCompleteData}
            placeholder={t('stepperFormTradeIn.searchHint')}
            getSelectedVal={onSuggestionSelected}
            textInputVariant='plain'
          />
        </div>
        {version === 'v2' && (
          <div className='powered-by'>
            <div className='powered-by__lbl'>{t('stepperForm.poweredBy')}</div>
            <img src={dabaduLogo} alt='Dabadu' className='img-fluid' />
          </div>
        )}
      </div>

      <div className='shapes'>
        <div className='shapes__item'>
          <img src={shape1} alt='Dabadu' className='img-fluid' />
        </div>
        <div className='shapes__item'>
          <img src={shape2} alt='Dabadu' className='img-fluid' />
        </div>
      </div>
    </>
  );
};

const VehicleSearchStep = React.forwardRef(VehicleSearch);

export default VehicleSearchStep;
